'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface CreateCITokenRequest {
  name: string;
  // undefined = server default (90 days); 0 = never expires; positive = lifetime in days.
  expiresInDays?: number;
}

interface CreateCITokenResult {
  // The raw secret, returned exactly once. Show it, then it is unrecoverable.
  token: string;
  tokenPrefix: string;
  name: string;
}

interface CreateCITokenError {
  status: number;
  message: string;
}

/**
 * Hook for creating a CI token (admin only on the server).
 * POST /api/v1/organization/ci-tokens
 *
 * Returns the raw token once; the caller must surface it immediately.
 */
export function useCreateCIToken() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    CreateCITokenResult,
    CreateCITokenError,
    CreateCITokenRequest
  >({
    mutationFn: async ({ name, expiresInDays }) => {
      const token = await getToken();
      if (!token) {
        throw { status: 401, message: 'No authentication token available' };
      }

      const { data, error, response } = await apiClient.POST(
        '/api/v1/organization/ci-tokens',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            name,
            expiresInDays: expiresInDays,
          },
        }
      );

      if (error || !response.ok || !data) {
        throw {
          status: response.status,
          message: 'Failed to create CI token',
        };
      }

      return {
        token: data.token,
        tokenPrefix: data.ciToken.tokenPrefix,
        name: data.ciToken.name,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-tokens'] });
    },
  });
}
